import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { SparklesIcon } from '@revfluence/fresh-icons/solid/esm';
import { ArrowsRotateIcon, ExpandIcon, XmarkIcon } from '@revfluence/fresh-icons/solid/esm';

interface AVAIconProps {
  clientId: string;
  userToken: string;
  clientName?: string;
  apiUrl?: string;
}

export const AVAIcon: React.FC<AVAIconProps> = ({
  clientId,
  userToken,
  clientName,
  apiUrl = 'http://localhost:4000/graphql'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Extract programId and offerId from the current URL path
  const extractIdsFromPath = () => {
    const pathname = location.pathname;
    const programMatch = pathname.match(/\/projects\/(\d+)/);
    const offerMatch = pathname.match(/\/offers\/(\d+)/);

    return {
      programId: programMatch ? programMatch[1] : null,
      offerId: offerMatch ? offerMatch[1] : null,
    };
  };

  const { programId, offerId } = extractIdsFromPath();

  // GraphQL API calls for session management
  const startSession = async (): Promise<string | null> => {
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
          'X-Client-ID': clientId,
        },
        body: JSON.stringify({
          query: `
            mutation StartSession {
              startSession
            }
          `,
        }),
      });

      const result = await response.json();
      if (result.data?.startSession) {
        console.log('🚀 Session started:', result.data.startSession);
        return result.data.startSession;
      }
      return null;
    } catch (error) {
      console.error('Failed to start session:', error);
      return null;
    }
  };

  const endSession = async (sessionId: string): Promise<void> => {
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
          'X-Client-ID': clientId,
        },
        body: JSON.stringify({
          query: `
            mutation EndSession($sessionId: String!) {
              endSession(sessionId: $sessionId)
            }
          `,
          variables: {
            sessionId,
          },
        }),
      });

      const result = await response.json();
      if (result.data?.endSession) {
        console.log('🏁 Session ended:', sessionId);
      }
    } catch (error) {
      console.error('Failed to end session:', error);
    }
  };

  // Simple handler for user prompts
  const handleUserPrompt = useCallback((prompt: string) => {
    console.log('Processing user prompt:', prompt);

    // Here you can do whatever you want with the user's prompt:
    // - Parse for automation keywords
    // - Call your APIs
    // - Update your application state
    // - Show notifications
    // etc.

    // Example: Check for automation keywords
    if (prompt.includes('automation') || prompt.includes('automate')) {
      console.log('User wants to create automation!');
    }

    if (prompt.includes('email')) {
      console.log('User mentioned email!');
    }
  }, []);

  // Handler for blueprint data received from AI
  const handleBlueprintReceived = useCallback((blueprint: any, metadata: any) => {
    console.log('Processing automation blueprint:', blueprint);
    if (blueprint && blueprint.name) {
      console.log(`✨ Automation created: "${blueprint.name}"`);

      // Send blueprint to parent window (automation builder)
      window.parent.postMessage({
        type: 'AVA_BLUEPRINT_CREATED',
        payload: {
          blueprint,
          metadata: {
            source: 'AVA',
            timestamp: Date.now(),
            sessionId,
            ...metadata,
          },
        },
      }, '*');
    }
  }, [sessionId]);

  // Simple PostMessage listener for user prompts
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Check if message is from AI iframe
      if (event.origin !== 'http://localhost:5173') {
        return;
      }

      // Listen for user prompts
      if (event.data && event.data.type === 'USER_PROMPT') {
        console.log('🎯 User typed in AI:', event.data.data.prompt);

        // Here you can process the user's prompt
        // Call your APIs, update state, show notifications, etc.
        handleUserPrompt(event.data.data.prompt);
      }

      // Listen for blueprint data
      if (event.data && event.data.type === 'ASPIREX_AI_BLUEPRINT_COMPLETE') {
        handleBlueprintReceived(event.data.blueprint, event.data.metadata);
      }

      // Listen for automation builder communication
      if (event.data && event.data.type === 'AUTOMATION_BLUEPRINT') {
        console.log('Received blueprint from automation builder:', event.data.payload);
        // You can store this blueprint or use it for context
      }

      if (event.data && event.data.type === 'REQUEST_BLUEPRINT') {
        console.log('Automation builder requested blueprint');
        // You can send back the current blueprint if available
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sessionId, handleUserPrompt, handleBlueprintReceived]);

  // Handle popup open with session creation
  const handleOpen = async () => {
    if (!isOpen) {
      const newSessionId = await startSession();
      if (newSessionId) {
        setSessionId(newSessionId);
        setIsOpen(true);
      }
    } else {
      setIsOpen(false);
    }
  };

  // Handle popup close with session cleanup
  const handleClose = async () => {
    if (sessionId) {
      await endSession(sessionId);
    }
    setSessionId(null);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={handleOpen}
        className="relative rounded-lg w-full h-auto max-w-[80px] hover:opacity-80 transition-opacity cursor-pointer"
      >
        <div className="flex flex-col items-center justify-center relative w-full">
          <div className="flex flex-col gap-2 items-center justify-center pb-1 pt-2 px-2 relative w-full">
            <div className="flex items-center justify-center relative shrink-0">
              <SparklesIcon className="size-4 text-[#16282d]" />
            </div>
            <div className="flex items-center justify-center relative shrink-0 w-full">
              <div className="flex items-center justify-center px-2 py-0 relative rounded-lg shrink-0 bg-[#8B5CF6]">
                <span className="font-inter text-[12px] font-normal leading-[20px] text-white whitespace-nowrap">
                  AVA
                </span>
              </div>
            </div>
          </div>
        </div>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40" />
          <div className="fixed left-[85px] bottom-[80px] w-[400px] h-[700px] bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
            <div className="flex flex-col h-full">
              <div className="flex items-center px-4 py-3 bg-white">
                <div className="flex items-baseline gap-2 flex-1">
                  <div className="bg-[#6c77de] flex items-center justify-center rounded-lg size-6">
                    <SparklesIcon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-[16px] font-medium text-[#16282d] font-['Inter'] translate-y-[-1px]">Your AI Assistance</span>
                </div>
                <div className="flex items-center">
                  <button className="w-6 h-6 bg-white rounded-lg flex items-center justify-center p-[6px] hover:bg-gray-50 transition-colors">
                    <ArrowsRotateIcon className="w-3 h-3 text-[#16282d]" />
                  </button>
                  <button className="w-6 h-6 bg-white rounded-lg flex items-center justify-center p-[6px] hover:bg-gray-50 transition-colors">
                    <ExpandIcon className="w-3 h-3 text-[#16282d]" />
                  </button>
                  <button
                    onClick={handleClose}
                    className="w-6 h-6 bg-white rounded-lg flex items-center justify-center p-[6px] hover:bg-gray-50 transition-colors"
                  >
                    <XmarkIcon className="w-3 h-3 text-[#16282d]" />
                  </button>
                </div>
              </div>
              <div className="w-full h-px bg-[#e5e8eb]" />
              <div className="flex-1 bg-white overflow-hidden">
                <iframe
                  src={`http://localhost:5173?clientId=${encodeURIComponent(clientId)}&userToken=${encodeURIComponent(userToken)}&clientName=${encodeURIComponent(clientName || '')}&apiUrl=${encodeURIComponent(apiUrl)}${programId ? `&programId=${encodeURIComponent(programId)}` : ''}${offerId ? `&offerId=${encodeURIComponent(offerId)}` : ''}${sessionId ? `&sessionId=${encodeURIComponent(sessionId)}` : ''}`}
                  className="w-full h-full border-0"
                  title="AVA Component"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
